import React, { cloneElement, isValidElement } from "react";
import { ReactElement, ReactNode } from "react";
import { DefaultValues, FieldValues } from "react-hook-form";
import { HookFormTabHeader } from "./HookFormTabHeader";

const hiddenStyle = { display: "none" };

interface Props<TRecord> {
  basePath?: string;
  className?: string;
  classes?: object;
  children?: ReactNode;
  contentClassName?: string;
  hidden?: boolean;
  icon?: ReactElement;
  intent?: "header" | "content";
  label: string | ReactElement;
  margin?: "none" | "normal" | "dense";
  path?: string;
  record?: DefaultValues<TRecord>;
  resource?: string;
  value?: string | number;
  variant?: "standard" | "outlined" | "filled";
}

export const HookFormTab = <TRecord extends FieldValues>(
  props: Props<TRecord>
) => {
  const {
    basePath,
    className,
    classes,
    contentClassName,
    children,
    hidden,
    icon,
    intent,
    label,
    margin,
    path,
    record,
    resource,
    variant,
    value,
    ...rest
  } = props;
  const renderHeader = () => (
    <HookFormTabHeader
      label={label}
      value={value}
      icon={icon}
      className={className}
      classes={classes}
      {...rest}
    />
  );

  const renderContent = () => (
    <span
      style={hidden ? hiddenStyle : undefined}
      className={contentClassName}
      id={`tabpanel-${value}`}
      aria-labelledby={`tabheader-${value}`}
      // Set undefined instead of false because WAI-ARIA Authoring Practices 1.1
      // notes that aria-hidden="false" currently behaves inconsistently across browsers.
      aria-hidden={hidden || undefined}>
      {React.Children.map(children, (input) => {
        if (!isValidElement(input)) {
          return undefined;
        }

        return (
          <div className="ra-input">
            {cloneElement(input, { margin, variant })}
          </div>
        );
      })}
    </span>
  );

  return intent === "header" ? renderHeader() : renderContent();
};

HookFormTab.displayName = "HookFormTab";
